import React, { useState, useRef, useEffect } from 'react';
import { AnimatedSection } from './AnimatedSection';
import { Lightbulb, Code } from 'lucide-react'; // icons

export const HowItWorks = ({ data, ideaSteps, developerSteps }) => {
  const [selectedCard, setSelectedCard] = useState(null);
  const stepsRef = useRef(null);

  useEffect(() => {
    // Scroll to top of this section on mount
    requestAnimationFrame(() => {
      const section = document.getElementById('how-it-works');
      if (section) {
        section.scrollIntoView({ behavior: 'smooth' });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    });
  }, []);

  if (!ideaSteps || !ideaSteps.steps || ideaSteps.steps.length === 0)
    return null;

  const handleCardClick = (cardType) => {
    setSelectedCard(cardType);
    setTimeout(() => {
      stepsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 200);
  };

  const StepList = ({ steps }) => (
    <div className="hiw-steps-wrapper">
      {steps.map((step, index) => (
        <AnimatedSection key={index}>
          <div className={`hiw-step-row ${index % 2 !== 0 ? 'reverse' : ''}`}>
            <div className="hiw-step-text">
              <div className="hiw-step-badge">Step {index + 1}</div>
              <h4>{step.title}</h4>
              <p>{step.text}</p>
            </div>
            <div className="hiw-step-media">
              <img src={step.image} alt={step.title} loading="lazy" />
            </div>
          </div>
        </AnimatedSection>
      ))}
    </div>
  );

  return (
    <section id="how-it-works" className="hiw-section">
      <div className="container">
        {/* HEADER */}
        <div className="hiw-header text-center">
          <h2>Choose Your Path</h2>
          <div className="hiw-divider"></div>
          <p>Are you here to build a vision or build the code?</p>
        </div>

        {/* CARDS SELECTION */}
        <div className="hiw-cards-container">
          {/* Innovator Card */}
          <div
            className={`hiw-card innovators ${selectedCard === 'idea' ? 'active' : ''}`}
            onClick={() => handleCardClick('idea')}
          >
            <div className="hiw-icon-box idea">
              <Lightbulb size={40} />
            </div>
            <h3>Innovators</h3>
            <p>
              Have a vision? Discover how Mahanc turns ideas into reality step
              by step.
            </p>
            <button className="hiw-card-btn">See Process</button>
          </div>

          {/* Maker Card */}
          <div
            className={`hiw-card makers ${selectedCard === 'developer' ? 'active' : ''}`}
            onClick={() => handleCardClick('developer')}
          >
            <div className="hiw-icon-box dev">
              <Code size={40} />
            </div>
            <h3>Makers</h3>
            <p>
              Developers & creators—learn how you can contribute to projects and
              grow.
            </p>
            <button className="hiw-card-btn">Contribution Guide</button>
          </div>
        </div>

        {/* STEPS DISPLAY */}
        <div ref={stepsRef} className="hiw-steps-container">
          <div
            className={`hiw-steps-content ${selectedCard === 'idea' ? 'show' : ''}`}
          >
            {selectedCard === 'idea' && <StepList steps={ideaSteps.steps} />}
          </div>

          <div
            className={`hiw-steps-content ${selectedCard === 'developer' ? 'show' : ''}`}
          >
            {selectedCard === 'developer' && (
              <StepList
                steps={
                  developerSteps?.steps?.length
                    ? developerSteps.steps
                    : ideaSteps.steps
                }
              />
            )}
          </div>
        </div>
      </div>

      <style jsx>{`
        .hiw-section {
          padding: 100px 0;
          background: #f9fafd; /* Light contrast background */
        }

        .hiw-header h2 {
          font-family: 'Raleway', sans-serif;
          font-weight: 800;
          font-size: 36px;
          color: #333;
          margin-bottom: 20px;
        }

        .hiw-header p {
          font-size: 18px;
          color: #666;
          margin-bottom: 50px;
        }

        .hiw-divider {
          width: 70px;
          height: 4px;
          background: linear-gradient(to right, #5ca9fb 0%, #6372ff 100%);
          margin: 0 auto 20px;
          border-radius: 2px;
        }

        /* --- CARDS --- */
        .hiw-cards-container {
          display: flex;
          justify-content: center;
          gap: 30px;
          margin-bottom: 60px;
          flex-wrap: wrap;
        }

        .hiw-card {
          background: #fff;
          border-radius: 20px;
          padding: 40px 30px;
          width: 100%;
          max-width: 400px;
          text-align: center;
          cursor: pointer;
          transition: all 0.3s ease;
          border: 2px solid transparent;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
          position: relative;
          overflow: hidden;
        }

        .hiw-card:hover {
          transform: translateY(-10px);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
        }

        .hiw-card.active {
          border-color: #6372ff;
          background: #fff;
        }

        .hiw-icon-box {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          margin: 0 auto 25px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 32px;
        }

        .hiw-icon-box.idea {
          background: linear-gradient(135deg, #f6d365 0%, #fda085 100%);
          box-shadow: 0 10px 20px rgba(253, 160, 133, 0.4);
        }

        .hiw-icon-box.dev {
          background: linear-gradient(135deg, #6372ff 0%, #5ca9fb 100%);
          box-shadow: 0 10px 20px rgba(99, 114, 255, 0.4);
        }

        .hiw-card h3 {
          font-family: 'Raleway', sans-serif;
          font-weight: 700;
          font-size: 24px;
          margin-bottom: 15px;
          color: #333;
        }

        .hiw-card p {
          color: #666;
          margin-bottom: 30px;
          line-height: 1.6;
        }

        .hiw-card-btn {
          background: transparent;
          border: 2px solid #eee;
          padding: 10px 25px;
          border-radius: 50px;
          font-weight: 600;
          color: #555;
          transition: all 0.3s;
          pointer-events: none; /* Let card click handle it */
        }

        .hiw-card:hover .hiw-card-btn,
        .hiw-card.active .hiw-card-btn {
          background: #333;
          border-color: #333;
          color: #fff;
        }

        /* --- STEPS --- */
        .hiw-steps-content {
          display: none;
          opacity: 0;
          transition: opacity 0.5s ease;
        }

        .hiw-steps-content.show {
          display: block;
          opacity: 1;
          animation: fadeIn 0.6s ease-out;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .hiw-steps-wrapper {
          display: flex;
          flex-direction: column;
          gap: 60px;
          padding-top: 40px;
        }

        .hiw-step-row {
          display: flex;
          align-items: center;
          gap: 50px;
          background: #fff;
          padding: 30px;
          border-radius: 20px;
          box-shadow: 0 5px 20px rgba(0, 0, 0, 0.03);
        }

        .hiw-step-row.reverse {
          flex-direction: row-reverse;
        }

        .hiw-step-text,
        .hiw-step-media {
          flex: 1;
        }

        .hiw-step-badge {
          display: inline-block;
          background: #eef2ff;
          color: #6372ff;
          padding: 5px 15px;
          border-radius: 20px;
          font-weight: 700;
          font-size: 14px;
          margin-bottom: 15px;
          text-transform: uppercase;
        }

        .hiw-step-text h4 {
          font-size: 24px;
          font-weight: 700;
          margin-bottom: 15px;
          color: #333;
        }

        .hiw-step-text p {
          color: #666;
          line-height: 1.7;
          font-size: 16px;
        }
        .hiw-step-media img {
          width: 100%;
          max-width: 350px;
          height: auto;
          margin: 0 auto;
          display: block;
          border-radius: 18px;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08);
          transition: all 0.35s ease; /* smooth animation */
          transform: translateY(0);
        }

        /* ✨ Hover effect — subtle lift + glow */
        .hiw-step-media img:hover {
          transform: translateY(-8px) scale(1.03);
          box-shadow: 0 15px 40px rgba(99, 114, 255, 0.25);
          filter: brightness(1.05);
        }

        /* MOBILE */
        @media (max-width: 768px) {
          .hiw-step-row,
          .hiw-step-row.reverse {
            flex-direction: column;
            text-align: left;
          }

          .hiw-card {
            margin: 0 auto;
          }

          .hiw-header h2 {
            font-size: 28px;
          }
        }
      `}</style>
    </section>
  );
};
